// Employee Master + Employee Detail — HR module (Khyati Portal / RSR International).
// List of employees with filters + a create modal, and a rich detail drawer with
// Profile / Salary / Attendance / Tentative Earnings sub-tabs.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { HRC, money, fmtDate, fmtTime, Card, SectionTitle, SubTabs, Th, Td, Table, Pill, Btn, Stat, Field, input, MonthPicker, Modal, Empty, Loading } from './hrkit';
import { EmployeeLetters } from './Letters';
import { useAuth } from '../../context/AuthContext';
import { reportError } from '../../utils/reportError';

// Bank block is hr/admin ONLY (matches the field-level gate on the employee API).
const CAN_BANK_ROLES = ['admin', 'hr'];
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT_TYPES = [['savings', 'Savings'], ['current', 'Current']];
const acctTypeLabel = (t) => (ACCOUNT_TYPES.find(x => x[0] === t) || [])[1] || (t || '');
// Validate the optional IFSC; returns an error string or null.
const ifscError = (v) => (v && !IFSC_RE.test(String(v).trim().toUpperCase()))
  ? 'IFSC must be 11 characters: 4 letters, a 0, then 6 letters/digits (e.g. HDFC0001234)' : null;

const TYPES = [
  { key: 'monthly', label: 'Monthly' },
  { key: 'daily', label: 'Daily Wage' },
  { key: 'piece_rate', label: 'Piece Rate' },
  { key: 'contractor', label: 'Contractor' },
];
const typeLabel = (t) => (TYPES.find(x => x.key === t) || {}).label || t || '—';
// A device code is present when ontime_emp_id holds something other than blanks.
const hasDeviceCode = (e) => String(e.ontime_emp_id || '').trim() !== '';
const TYPE_COLOR = { monthly: HRC.navy, daily: HRC.amber, piece_rate: '#8B5CF6', contractor: HRC.green };

// Pay summary per employee row.
function payText(e) {
  switch (e.employee_type) {
    case 'monthly': return e.monthly_ctc ? `${money(e.monthly_ctc)}/mo` : '—';
    case 'daily': return e.daily_rate ? `${money(e.daily_rate)}/day` : '—';
    case 'piece_rate': return e.piece_rate ? `${money(e.piece_rate)}/unit` : '—';
    case 'contractor': return e.contract_amount ? `${money(e.contract_amount)}/mo` : '—';
    default: return '—';
  }
}

export default function EmployeeMaster(props) {
  const { month, onChange } = props;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [depts, setDepts] = useState([]);

  // filters
  const [q, setQ] = useState('');
  const [dept, setDept] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('active');

  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);

  // DEVICE CODE (ontime_emp_id), READ-ONLY HERE (24 Oct 2026). The attendance feed matches punches on it, so an
  // active employee without one receives no attendance. The count is of ALL active employees in the company, read on
  // its own so the search and filters cannot change it; the toggle narrows what is shown. Editing stays in the
  // employee's own record (mig 403 guards it there).
  const [onlyNoCode, setOnlyNoCode] = useState(false);
  const [codeCount, setCodeCount] = useState(null);   // { active, missing }

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (dept) params.dept = dept;
    if (type) params.type = type;
    if (status) params.status = status;
    api.get('/hr/employees', { params })
      .then(r => setRows(r.data.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [q, dept, type, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/hr/departments').then(r => setDepts(r.data.data || [])).catch(() => {});
  }, []);

  const loadCodeCount = useCallback(() => {
    api.get('/hr/employees', { params: { status: 'active' } })
      .then(r => { const all = r.data.data || []; setCodeCount({ active: all.length, missing: all.filter(e => !hasDeviceCode(e)).length }); })
      .catch(() => setCodeCount(null));
  }, []);
  useEffect(() => { loadCodeCount(); }, [loadCodeCount]);

  const refresh = useCallback(() => { load(); loadCodeCount(); onChange?.(); }, [load, loadCodeCount, onChange]);
  // ACTIVE ONLY (Rajan, 24 Oct 2026). An inactive or exited employee with no device code is not a gap to fill, and
  // filling it is the reactivation-collision risk (mig 403 does not check a reactivation). So the toggle shows the
  // gap the count line counts, whatever the status filter is set to.
  const shown = onlyNoCode ? rows.filter(e => e.status === 'active' && !hasDeviceCode(e)) : rows;

  const counts = TYPES.map(t => ({ ...t, n: rows.filter(r => r.employee_type === t.key).length }));

  const sel = { ...input, width: 'auto', minWidth: 150 };

  return (
    <div>
      {/* Stat tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 16 }}>
        <Stat label="Employees Shown" value={rows.length} sub={status ? `${status} filter` : 'all statuses'} />
        {counts.map(c => (
          <Stat key={c.key} label={c.label} value={c.n} accent={TYPE_COLOR[c.key]} />
        ))}
      </div>

      <Card>
        <SectionTitle right={<Btn onClick={() => setShowCreate(true)}>+ Add Employee</Btn>}>Employees</SectionTitle>

        {/* Filter bar */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
          <input
            placeholder="Search name / code / phone…"
            value={q}
            onChange={e => setQ(e.target.value)}
            style={{ ...input, width: 'auto', minWidth: 220, flex: '1 1 220px' }}
          />
          <select value={dept} onChange={e => setDept(e.target.value)} style={sel}>
            <option value="">All Departments</option>
            {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={type} onChange={e => setType(e.target.value)} style={sel}>
            <option value="">All Pay Types</option>
            {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <select value={status} onChange={e => setStatus(e.target.value)} style={sel}>
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="exited">Exited</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: HRC.text, cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyNoCode} onChange={e => setOnlyNoCode(e.target.checked)} />
            Only active employees missing a device code
          </label>
          {(q || dept || type || status !== 'active' || onlyNoCode) && (
            <Btn variant="ghost" size="sm" onClick={() => { setQ(''); setDept(''); setType(''); setStatus('active'); setOnlyNoCode(false); }}>Reset</Btn>
          )}
        </div>

        {codeCount && (
          <div style={{ fontSize: 13, marginBottom: 12, color: codeCount.missing ? HRC.amber : HRC.green, fontWeight: 600 }}>
            {codeCount.missing} of {codeCount.active} active employees have no device code
            {codeCount.missing > 0 && <span style={{ fontWeight: 400, color: HRC.muted }}> — their punches cannot reach attendance until one is recorded on their record.</span>}
          </div>
        )}

        {loading ? <Loading /> : shown.length === 0 ? <Empty>{onlyNoCode && rows.length ? 'Every active employee shown has a device code.' : 'No employees match these filters.'}</Empty> : (
          <Table>
            <thead>
              <tr>
                <Th>Code</Th>
                <Th>Device code</Th>
                <Th>Name</Th>
                <Th>Department</Th>
                <Th>Designation</Th>
                <Th>Type</Th>
                <Th>Pay</Th>
                <Th>Status</Th>
                <Th style={{ textAlign: 'right' }}></Th>
              </tr>
            </thead>
            <tbody>
              {shown.map(e => (
                <tr key={e.id}>
                  <Td style={{ fontWeight: 700, color: HRC.navy }}>{e.employee_code || '—'}</Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>{hasDeviceCode(e)
                    ? <span style={{ fontFamily: 'monospace' }}>{String(e.ontime_emp_id).trim()}</span>
                    : <span style={{ color: HRC.muted }} title="No device code recorded">—</span>}</Td>
                  <Td>
                    <div style={{ fontWeight: 600 }}>{e.full_name}</div>
                    {e.father_name && <div style={{ fontSize: 11, color: HRC.muted }}>s/o {e.father_name}</div>}
                  </Td>
                  <Td>{e.department_name || '—'}</Td>
                  <Td>{e.designation || '—'}</Td>
                  <Td><span style={{ color: TYPE_COLOR[e.employee_type] || HRC.text, fontWeight: 700, fontSize: 12 }}>{typeLabel(e.employee_type)}</span></Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>{payText(e)}</Td>
                  <Td><Pill value={e.status} /></Td>
                  <Td style={{ textAlign: 'right' }}>
                    <Btn size="sm" variant="soft" onClick={() => setDetailId(e.id)}>View</Btn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {showCreate && (
        <CreateEmployee
          depts={depts}
          onClose={() => setShowCreate(false)}
          onSaved={(id) => { setShowCreate(false); refresh(); if (id) setDetailId(id); }}
        />
      )}

      {detailId && (
        <EmployeeDetail
          id={detailId}
          depts={depts}
          month={month}
          onClose={() => setDetailId(null)}
          onChange={refresh}
        />
      )}
    </div>
  );
}

/* ───────────────────────── Create Employee ───────────────────────── */
function CreateEmployee({ depts, onClose, onSaved }) {
  const { user } = useAuth();
  const canBank = CAN_BANK_ROLES.includes(user?.role);
  const [f, setF] = useState({
    employee_code: '', full_name: '', employee_type: 'monthly',
    department_id: '', designation_id: '', father_name: '', gender: '',
    dob: '', phone: '', email: '', date_of_joining: '', ontime_emp_id: '',
    shift_start: '09:30', shift_end: '18:00',
    account_holder_name: '', bank_account_no: '', bank_name: '', ifsc: '', account_type: '',
  });
  const [desigs, setDesigs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    if (!f.department_id) { setDesigs([]); return; }
    api.get('/hr/designations', { params: { department_id: f.department_id } })
      .then(r => setDesigs(r.data.data || [])).catch(() => setDesigs([]));
  }, [f.department_id]);

  const submit = () => {
    if (!f.full_name.trim()) { setErr('Full name is required'); return; }
    if (canBank) { const be = ifscError(f.ifsc); if (be) { setErr(be); return; } }
    setSaving(true); setErr('');
    api.post('/hr/employees', f)
      .then(r => onSaved(r.data?.data?.id))
      .catch(e => setErr(e.response?.data?.message || 'Failed to create employee'))
      .finally(() => setSaving(false));
  };

  return (
    <Modal title="Add Employee" onClose={onClose} width={640}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Employee Code"><input style={input} value={f.employee_code} onChange={set('employee_code')} placeholder="EMP001" /></Field>
        <Field label="Full Name *"><input style={input} value={f.full_name} onChange={set('full_name')} /></Field>
        <Field label="Pay Type">
          <select style={input} value={f.employee_type} onChange={set('employee_type')}>
            {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Father's Name"><input style={input} value={f.father_name} onChange={set('father_name')} /></Field>
        <Field label="Department">
          <select style={input} value={f.department_id} onChange={(e) => { set('department_id')(e); setF(p => ({ ...p, designation_id: '' })); }}>
            <option value="">— Select —</option>
            {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Designation">
          <select style={input} value={f.designation_id} onChange={set('designation_id')} disabled={!f.department_id}>
            <option value="">— Select —</option>
            {desigs.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        </Field>
        <Field label="Gender">
          <select style={input} value={f.gender} onChange={set('gender')}>
            <option value="">—</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Date of Birth"><input type="date" style={input} value={f.dob} onChange={set('dob')} /></Field>
        <Field label="Phone"><input style={input} value={f.phone} onChange={set('phone')} /></Field>
        <Field label="Email"><input style={input} value={f.email} onChange={set('email')} /></Field>
        <Field label="Date of Joining"><input type="date" style={input} value={f.date_of_joining} onChange={set('date_of_joining')} /></Field>
        <Field label="OnTime Emp ID"><input style={input} value={f.ontime_emp_id} onChange={set('ontime_emp_id')} placeholder="biometric id" /></Field>
        <Field label="Shift Start"><input type="time" style={input} value={f.shift_start} onChange={set('shift_start')} /></Field>
        <Field label="Shift End"><input type="time" style={input} value={f.shift_end} onChange={set('shift_end')} /></Field>
      </div>
      {canBank && (
        <>
          <SubHead>Bank Details</SubHead>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Account Holder Name"><input style={input} value={f.account_holder_name} onChange={set('account_holder_name')} /></Field>
            <Field label="Bank Account No"><input style={input} value={f.bank_account_no} onChange={set('bank_account_no')} /></Field>
            <Field label="Bank Name"><input style={input} value={f.bank_name} onChange={set('bank_name')} /></Field>
            <Field label="IFSC"><input style={input} value={f.ifsc} onChange={e => setF(p => ({ ...p, ifsc: e.target.value.toUpperCase() }))} placeholder="HDFC0001234" maxLength={11} /></Field>
            <Field label="Account Type">
              <select style={input} value={f.account_type} onChange={set('account_type')}>
                <option value="">—</option>
                {ACCOUNT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
          </div>
        </>
      )}
      {err && <div style={{ color: HRC.red, fontSize: 12, marginTop: 12 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Create Employee'}</Btn>
      </div>
    </Modal>
  );
}

/* ───────────────────────── Employee Detail ───────────────────────── */
function EmployeeDetail({ id, depts, month, onClose, onChange }) {
  const [emp, setEmp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('profile');

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/hr/employees/${id}`)
      .then(r => setEmp(r.data.data))
      .catch(() => setEmp(null))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const refresh = useCallback(() => { load(); onChange?.(); }, [load, onChange]);

  return (
    <Modal title="Employee" onClose={onClose} width={760}>
      {loading ? <Loading /> : !emp ? <Empty>Could not load employee.</Empty> : (
        <div>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 19, fontWeight: 800, color: HRC.text }}>{emp.full_name}</div>
              <div style={{ fontSize: 12, color: HRC.muted, marginTop: 2 }}>
                <strong style={{ color: HRC.navy }}>{emp.employee_code || '—'}</strong>
                {' · '}{emp.department_name || '—'}{emp.designation ? ` · ${emp.designation}` : ''}
              </div>
              <div style={{ fontSize: 11, color: HRC.muted, marginTop: 4 }}>
                {emp.portal_user ? `Portal login: ${emp.portal_user}` : 'No portal login'}
                {emp.reporting_manager ? ` · Reports to ${emp.reporting_manager}` : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
              <Pill value={emp.status} />
              <span style={{ color: TYPE_COLOR[emp.employee_type] || HRC.text, fontWeight: 700, fontSize: 12 }}>{typeLabel(emp.employee_type)}</span>
            </div>
          </div>

          <SubTabs
            tabs={[
              { key: 'profile', label: 'Profile' },
              { key: 'salary', label: 'Salary' },
              { key: 'attendance', label: 'Attendance' },
              { key: 'tentative', label: 'Tentative Earnings' },
              { key: 'letters', label: 'Letters' },
              { key: 'documents', label: 'Documents' },
            ]}
            active={tab}
            onChange={setTab}
          />

          {tab === 'profile' && <ProfileTab emp={emp} depts={depts} onSaved={refresh} />}
          {tab === 'salary' && <SalaryTab emp={emp} onSaved={refresh} />}
          {tab === 'attendance' && <AttendanceTab empId={emp.id} defaultMonth={month} />}
          {tab === 'tentative' && <TentativeTab emp={emp} defaultMonth={month} />}
          {tab === 'letters' && <EmployeeLetters employeeId={emp.id} />}
          {tab === 'documents' && <EmployeeDocuments employeeId={emp.id} />}
        </div>
      )}
    </Modal>
  );
}

/* ── Profile ── */
function ReadField({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: HRC.text }}>{value || '—'}</div>
    </div>
  );
}

// Section subheading inside the profile.
function SubHead({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: HRC.navy, textTransform: 'uppercase', letterSpacing: 0.5,
    margin: '20px 0 10px', paddingBottom: 6, borderBottom: `2px solid ${HRC.violet}` }}>{children}</div>;
}

// Spouse label flips on gender (backend stays a single spouse_name field).
const spouseLabel = (emp) => emp?.gender === 'female' ? 'Husband Name' : emp?.gender === 'male' ? 'Wife Name' : 'Spouse Name';

// Live children/dependents CRUD (mig 063). Reads emp.family; add/remove hit the API.
function ChildrenPanel({ employeeId, family, onChanged }) {
  const [adding, setAdding] = useState({ relation: 'child', name: '', dob: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setAdding(p => ({ ...p, [k]: e.target.value }));
  const add = () => {
    if (!adding.name.trim()) return;
    setBusy(true);
    api.post(`/hr/employees/${employeeId}/family`, adding)
      .then(() => { setAdding({ relation: 'child', name: '', dob: '' }); onChanged?.(); })
      .catch(reportError('add this dependent'))
      .finally(() => setBusy(false));
  };
  const remove = (id) => api.delete(`/hr/family/${id}`).then(() => onChanged?.()).catch(reportError('remove this dependent'));
  const rows = family || [];
  return (
    <div>
      <SubHead>Children / Dependents</SubHead>
      {rows.length === 0 && <div style={{ fontSize: 12, color: HRC.muted, marginBottom: 8 }}>No children/dependents added.</div>}
      {rows.map(c => (
        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #F4F3F0' }}>
          <span style={{ flex: '0 0 70px', fontSize: 11, color: HRC.muted, textTransform: 'capitalize' }}>{c.relation}</span>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{c.name}</span>
          <span style={{ flex: '0 0 110px', fontSize: 12, color: HRC.muted }}>{c.dob ? fmtDate(c.dob) : '—'}</span>
          <Btn size="sm" variant="ghost" onClick={() => remove(c.id)}>Remove</Btn>
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginTop: 10 }}>
        <select style={{ ...input, width: 110 }} value={adding.relation} onChange={set('relation')}>
          <option value="child">Child</option><option value="other">Other</option>
        </select>
        <input style={{ ...input, flex: 1 }} placeholder="Name" value={adding.name} onChange={set('name')} />
        <input type="date" style={{ ...input, width: 150 }} value={adding.dob} onChange={set('dob')} />
        <Btn size="sm" onClick={add} disabled={busy || !adding.name.trim()}>+ Add</Btn>
      </div>
    </div>
  );
}

function ProfileTab({ emp, depts, onSaved }) {
  const { user } = useAuth();
  const canBank = CAN_BANK_ROLES.includes(user?.role);
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [f, setF] = useState({});
  const [desigs, setDesigs] = useState([]);

  const start = () => {
    setF({
      full_name: emp.full_name || '', father_name: emp.father_name || '', gender: emp.gender || '',
      dob: (emp.dob || '').slice(0, 10), phone: emp.phone || '', email: emp.email || '',
      date_of_joining: (emp.date_of_joining || '').slice(0, 10), date_of_leaving: (emp.date_of_leaving || '').slice(0, 10),
      ontime_emp_id: emp.ontime_emp_id || '',
      department_id: emp.department_id || '', designation_id: emp.designation_id || '',
      employee_type: emp.employee_type || 'monthly', status: emp.status || 'active',
      shift_start: fmtTime(emp.shift_start) === '—' ? '' : fmtTime(emp.shift_start),
      shift_end: fmtTime(emp.shift_end) === '—' ? '' : fmtTime(emp.shift_end),
      // statutory + family (mig 063 / 061)
      esi_no: emp.esi_no || '', pf_no: emp.pf_no || '', uan: emp.uan || '',
      marital_status: emp.marital_status || '', mother_name: emp.mother_name || '',
      spouse_name: emp.spouse_name || '', spouse_dob: (emp.spouse_dob || '').slice(0, 10),
      emergency_contact: emp.emergency_contact || '', emergency_contact_relation: emp.emergency_contact_relation || '',
      // bank (mig 142 + 036) — only sent when the caller is hr/admin
      account_holder_name: emp.account_holder_name || '', bank_account_no: emp.bank_account_no || '',
      bank_name: emp.bank_name || '', ifsc: emp.ifsc || '', account_type: emp.account_type || '',
    });
    setErr(''); setEdit(true);
  };
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    if (!edit || !f.department_id) { setDesigs([]); return; }
    api.get('/hr/designations', { params: { department_id: f.department_id } })
      .then(r => setDesigs(r.data.data || [])).catch(() => setDesigs([]));
  }, [edit, f.department_id]);

  const save = () => {
    if (canBank) { const be = ifscError(f.ifsc); if (be) { setErr(be); return; } }
    setSaving(true); setErr('');
    api.patch(`/hr/employees/${emp.id}`, f)
      .then(() => { setEdit(false); onSaved?.(); })
      .catch(e => setErr(e.response?.data?.error || e.response?.data?.message || 'Failed to save'))
      .finally(() => setSaving(false));
  };

  const shift = (fmtTime(emp.shift_start) !== '—' || fmtTime(emp.shift_end) !== '—')
    ? `${fmtTime(emp.shift_start)} – ${fmtTime(emp.shift_end)}` : '—';

  if (!edit) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Btn size="sm" variant="ghost" onClick={start}>Edit</Btn>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 14 }}>
          <ReadField label="Father's Name" value={emp.father_name} />
          <ReadField label="Gender" value={emp.gender} />
          <ReadField label="Date of Birth" value={emp.dob ? fmtDate(emp.dob) : ''} />
          <ReadField label="Phone" value={emp.phone} />
          <ReadField label="Email" value={emp.email} />
          <ReadField label="Date of Joining" value={emp.date_of_joining ? fmtDate(emp.date_of_joining) : ''} />
          <ReadField label="Date of Leaving" value={emp.date_of_leaving ? fmtDate(emp.date_of_leaving) : ''} />
          <ReadField label="OnTime Emp ID" value={emp.ontime_emp_id} />
          <ReadField label="Shift" value={shift} />
        </div>

        <SubHead>Statutory</SubHead>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 14 }}>
          <ReadField label="ESI No" value={emp.esi_no} />
          <ReadField label="PF No" value={emp.pf_no} />
          <ReadField label="UAN" value={emp.uan} />
        </div>

        {canBank && (
          <>
            <SubHead>Bank Details</SubHead>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 14 }}>
              <ReadField label="Account Holder" value={emp.account_holder_name} />
              <ReadField label="Bank Account No" value={emp.bank_account_no} />
              <ReadField label="Bank Name" value={emp.bank_name} />
              <ReadField label="IFSC" value={emp.ifsc} />
              <ReadField label="Account Type" value={acctTypeLabel(emp.account_type)} />
            </div>
          </>
        )}

        <SubHead>Family</SubHead>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 14 }}>
          <ReadField label="Mother's Name" value={emp.mother_name} />
          <ReadField label={spouseLabel(emp)} value={emp.spouse_name} />
          <ReadField label="Spouse DOB" value={emp.spouse_dob ? fmtDate(emp.spouse_dob) : ''} />
          <ReadField label="Emergency Contact" value={emp.emergency_contact} />
          <ReadField label="Emergency Relation" value={emp.emergency_contact_relation} />
        </div>

        <ChildrenPanel employeeId={emp.id} family={emp.family} onChanged={onSaved} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Full Name"><input style={input} value={f.full_name} onChange={set('full_name')} /></Field>
        <Field label="Father's Name"><input style={input} value={f.father_name} onChange={set('father_name')} /></Field>
        <Field label="Pay Type">
          <select style={input} value={f.employee_type} onChange={set('employee_type')}>
            {TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select style={input} value={f.status} onChange={set('status')}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="exited">Exited</option>
          </select>
        </Field>
        <Field label="Department">
          <select style={input} value={f.department_id} onChange={(e) => { set('department_id')(e); setF(p => ({ ...p, designation_id: '' })); }}>
            <option value="">— Select —</option>
            {depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="Designation">
          <select style={input} value={f.designation_id} onChange={set('designation_id')} disabled={!f.department_id}>
            <option value="">— Select —</option>
            {desigs.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        </Field>
        <Field label="Gender">
          <select style={input} value={f.gender} onChange={set('gender')}>
            <option value="">—</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Date of Birth"><input type="date" style={input} value={f.dob} onChange={set('dob')} /></Field>
        <Field label="Phone"><input style={input} value={f.phone} onChange={set('phone')} /></Field>
        <Field label="Email"><input style={input} value={f.email} onChange={set('email')} /></Field>
        <Field label="Date of Joining"><input type="date" style={input} value={f.date_of_joining} onChange={set('date_of_joining')} /></Field>
        {/* The relieving letter prints it (31 Oct 2026); no screen could set it before. */}
        <Field label="Date of Leaving"><input type="date" style={input} value={f.date_of_leaving} onChange={set('date_of_leaving')} /></Field>
        <Field label="OnTime Emp ID"><input style={input} value={f.ontime_emp_id} onChange={set('ontime_emp_id')} /></Field>
        <Field label="Shift Start"><input type="time" style={input} value={f.shift_start} onChange={set('shift_start')} /></Field>
        <Field label="Shift End"><input type="time" style={input} value={f.shift_end} onChange={set('shift_end')} /></Field>
      </div>

      <SubHead>Statutory</SubHead>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="ESI No"><input style={input} value={f.esi_no} onChange={set('esi_no')} /></Field>
        <Field label="PF No"><input style={input} value={f.pf_no} onChange={set('pf_no')} /></Field>
        <Field label="UAN"><input style={input} value={f.uan} onChange={set('uan')} /></Field>
      </div>

      {canBank && (
        <>
          <SubHead>Bank Details</SubHead>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Account Holder Name"><input style={input} value={f.account_holder_name} onChange={set('account_holder_name')} /></Field>
            <Field label="Bank Account No"><input style={input} value={f.bank_account_no} onChange={set('bank_account_no')} /></Field>
            <Field label="Bank Name"><input style={input} value={f.bank_name} onChange={set('bank_name')} /></Field>
            <Field label="IFSC"><input style={input} value={f.ifsc} onChange={e => setF(p => ({ ...p, ifsc: e.target.value.toUpperCase() }))} placeholder="HDFC0001234" maxLength={11} /></Field>
            <Field label="Account Type">
              <select style={input} value={f.account_type} onChange={set('account_type')}>
                <option value="">—</option>
                {ACCOUNT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
          </div>
        </>
      )}

      <SubHead>Family</SubHead>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Marital Status">
          <select style={input} value={f.marital_status} onChange={set('marital_status')}>
            <option value="">—</option><option value="single">Single</option><option value="married">Married</option><option value="other">Other</option>
          </select>
        </Field>
        <Field label="Mother's Name"><input style={input} value={f.mother_name} onChange={set('mother_name')} /></Field>
        <Field label={spouseLabel(f.gender ? f : emp)}><input style={input} value={f.spouse_name} onChange={set('spouse_name')} /></Field>
        <Field label="Spouse DOB"><input type="date" style={input} value={f.spouse_dob} onChange={set('spouse_dob')} /></Field>
        <Field label="Emergency Contact"><input style={input} value={f.emergency_contact} onChange={set('emergency_contact')} /></Field>
        <Field label="Emergency Relation"><input style={input} value={f.emergency_contact_relation} onChange={set('emergency_contact_relation')} placeholder="e.g. Father, Spouse" /></Field>
      </div>

      {err && <div style={{ color: HRC.red, fontSize: 12, marginTop: 12 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Btn variant="ghost" onClick={() => setEdit(false)}>Cancel</Btn>
        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Btn>
      </div>

      <ChildrenPanel employeeId={emp.id} family={emp.family} onChanged={onSaved} />
    </div>
  );
}

/* ── Salary ── */
function SalaryRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid #F1F0EE`, fontSize: 13 }}>
      <span style={{ color: HRC.muted }}>{label}</span>
      <span style={{ fontWeight: 700 }}>{value}</span>
    </div>
  );
}

function SalaryTab({ emp, onSaved }) {
  const t = emp.employee_type;
  const sal = emp.salary;
  const comps = emp.components || [];
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [f, setF] = useState({
    pay_type: t, monthly_ctc: '', basic: '', hra: '', conveyance: '', special_allowance: '',
    daily_rate: '', piece_rate: '', contract_amount: '', overtime_rate_per_hour: '',
    effective_from: '',
  });
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));

  const save = () => {
    setSaving(true); setErr('');
    const payload = { ...f };
    Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
    payload.pay_type = t;
    api.post(`/hr/employees/${emp.id}/salary`, payload)
      .then(() => { setShowForm(false); onSaved?.(); })
      .catch(e => setErr(e.response?.data?.error || e.response?.data?.message || 'Failed to save salary structure'))
      .finally(() => setSaving(false));
  };

  return (
    <div>
      <SectionTitle right={<Btn size="sm" onClick={() => setShowForm(s => !s)}>{showForm ? 'Close' : '+ New Structure'}</Btn>}>
        Active Salary Structure
      </SectionTitle>

      {!sal ? <Empty>No salary structure set for this employee.</Empty> : (
        <div style={{ maxWidth: 420 }}>
          {sal.effective_from && (
            <div style={{ fontSize: 11, color: HRC.muted, marginBottom: 6 }}>Effective from {fmtDate(sal.effective_from)}</div>
          )}
          {t === 'monthly' && (
            <>
              {sal.monthly_ctc != null && <SalaryRow label="Monthly CTC" value={money(sal.monthly_ctc)} />}
              {sal.basic != null && <SalaryRow label="Basic" value={money(sal.basic)} />}
              {sal.hra != null && <SalaryRow label="HRA" value={money(sal.hra)} />}
              {sal.conveyance != null && <SalaryRow label="Conveyance" value={money(sal.conveyance)} />}
              {sal.special_allowance != null && <SalaryRow label="Special Allowance" value={money(sal.special_allowance)} />}
            </>
          )}
          {t === 'daily' && sal.daily_rate != null && <SalaryRow label="Daily Rate" value={`${money(sal.daily_rate)}/day`} />}
          {t === 'piece_rate' && sal.piece_rate != null && <SalaryRow label="Piece Rate" value={`${money(sal.piece_rate)}/unit`} />}
          {t === 'contractor' && sal.contract_amount != null && <SalaryRow label="Contract Amount" value={`${money(sal.contract_amount)}/mo`} />}
          {sal.overtime_rate_per_hour != null && <SalaryRow label="Overtime Rate" value={`${money(sal.overtime_rate_per_hour)}/hr`} />}
        </div>
      )}

      {comps.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <SectionTitle>Components</SectionTitle>
          <Table>
            <thead>
              <tr><Th>Component</Th><Th>Type</Th><Th>Calc</Th><Th style={{ textAlign: 'right' }}>Amount</Th></tr>
            </thead>
            <tbody>
              {comps.map((c, i) => (
                <tr key={i}>
                  <Td>{c.component_name}</Td>
                  <Td style={{ textTransform: 'capitalize' }}>{(c.component_type || '').replace(/_/g, ' ')}</Td>
                  <Td style={{ textTransform: 'capitalize' }}>{(c.calc_type || '').replace(/_/g, ' ')}</Td>
                  <Td style={{ textAlign: 'right' }}>{c.calc_type === 'percent' ? `${c.amount}%` : money(c.amount)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {showForm && (
        <Card style={{ marginTop: 18, background: HRC.violet, border: `1px solid ${HRC.border}` }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>
            New Salary Structure — {typeLabel(t)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {t === 'monthly' && (
              <>
                <Field label="Monthly CTC"><input type="number" style={input} value={f.monthly_ctc} onChange={set('monthly_ctc')} /></Field>
                <Field label="Basic"><input type="number" style={input} value={f.basic} onChange={set('basic')} /></Field>
                <Field label="HRA"><input type="number" style={input} value={f.hra} onChange={set('hra')} /></Field>
                <Field label="Conveyance"><input type="number" style={input} value={f.conveyance} onChange={set('conveyance')} /></Field>
                <Field label="Special Allowance"><input type="number" style={input} value={f.special_allowance} onChange={set('special_allowance')} /></Field>
              </>
            )}
            {t === 'daily' && <Field label="Daily Rate"><input type="number" style={input} value={f.daily_rate} onChange={set('daily_rate')} /></Field>}
            {t === 'piece_rate' && <Field label="Piece Rate (per unit)"><input type="number" style={input} value={f.piece_rate} onChange={set('piece_rate')} /></Field>}
            {t === 'contractor' && <Field label="Contract Amount"><input type="number" style={input} value={f.contract_amount} onChange={set('contract_amount')} /></Field>}
            <Field label="Overtime Rate / hr"><input type="number" style={input} value={f.overtime_rate_per_hour} onChange={set('overtime_rate_per_hour')} /></Field>
            <Field label="Effective From"><input type="date" style={input} value={f.effective_from} onChange={set('effective_from')} /></Field>
          </div>
          {err && <div style={{ color: HRC.red, fontSize: 12, marginTop: 10 }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
            <Btn variant="ghost" onClick={() => setShowForm(false)}>Cancel</Btn>
            <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Structure'}</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ── Attendance ── */
function AttendanceTab({ empId, defaultMonth }) {
  const [m, setM] = useState(defaultMonth || new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/hr/attendance', { params: { employee_id: empId, month: m } })
      .then(r => setRows(r.data.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [empId, m]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <MonthPicker value={m} onChange={setM} />
      </div>
      {loading ? <Loading /> : rows.length === 0 ? <Empty>No attendance for {m}.</Empty> : (
        <Table>
          <thead>
            <tr>
              <Th>Date</Th><Th>Status</Th><Th>In</Th><Th>Out</Th>
              <Th style={{ textAlign: 'right' }}>Worked</Th>
              <Th style={{ textAlign: 'right' }}>OT</Th>
              <Th style={{ textAlign: 'right' }}>Late</Th>
              <Th style={{ textAlign: 'right' }}>Units</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <Td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.work_date)}</Td>
                <Td>
                  <Pill value={r.status} />
                  {r.is_miss_punch ? <span style={{ color: HRC.red, fontSize: 10, marginLeft: 4 }}>miss</span> : null}
                </Td>
                <Td>{fmtTime(r.check_in)}</Td>
                <Td>{fmtTime(r.check_out)}</Td>
                <Td style={{ textAlign: 'right' }}>{r.worked_hours != null ? Number(r.worked_hours).toFixed(1) : '—'}</Td>
                <Td style={{ textAlign: 'right' }}>{r.ot_hours ? Number(r.ot_hours).toFixed(1) : '—'}</Td>
                <Td style={{ textAlign: 'right' }}>{r.late_minutes ? `${r.late_minutes}m` : '—'}</Td>
                <Td style={{ textAlign: 'right' }}>{r.units_produced || '—'}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

/* ── Tentative Earnings ── */
function Metric({ label, value, accent }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${HRC.border}`, borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: accent || HRC.text, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function TentativeTab({ emp, defaultMonth }) {
  const [m, setM] = useState(defaultMonth || new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isPiece = emp.employee_type === 'piece_rate';

  useEffect(() => {
    setLoading(true);
    api.get(`/hr/employees/${emp.id}/tentative`, { params: { month: m } })
      .then(r => setData(r.data.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [emp.id, m]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{
          fontSize: 12, fontWeight: 700, color: HRC.navy, background: HRC.violet,
          padding: '5px 12px', borderRadius: 20,
        }}>
          {isPiece ? 'Calculated by output (units × rate)' : 'Calculated by days + hours'}
        </div>
        <MonthPicker value={m} onChange={setM} />
      </div>

      {loading ? <Loading /> : !data ? <Empty>No data for {m}.</Empty> : (
        <div>
          {/* Big gross earnings */}
          <Card style={{ background: HRC.navy, border: 'none', marginBottom: 14 }}>
            <div style={{ color: '#C9C8F0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>Gross Earnings (tentative)</div>
            <div style={{ color: '#fff', fontSize: 34, fontWeight: 800, marginTop: 4 }}>{money(data.gross_earnings)}</div>
            <div style={{ color: '#C9C8F0', fontSize: 11, marginTop: 4 }}>
              Tentative as of {data.upto ? fmtDate(data.upto) : '—'} · {m}
            </div>
          </Card>

          {/* Day counts */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(110px,1fr))', gap: 10, marginBottom: 14 }}>
            <Metric label="Present Days" value={data.present_days ?? '—'} accent={HRC.green} />
            <Metric label="Paid Days" value={data.paid_days ?? '—'} accent={HRC.navy} />
            <Metric label="LOP Days" value={data.lop_days ?? '—'} accent={HRC.red} />
            <Metric label="OT Hours" value={data.ot_hours ?? '—'} accent={HRC.amber} />
            {isPiece && <Metric label="Units" value={data.units ?? '—'} accent="#8B5CF6" />}
          </div>

          {/* Day-type breakdown */}
          <div style={{ fontSize: 11, color: HRC.muted, marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            <span>Full: <strong>{data.present_full ?? 0}</strong></span>
            <span>Half: <strong>{data.half ?? 0}</strong></span>
            <span>Leave: <strong>{data.leave_days ?? 0}</strong></span>
            <span>Week-off: <strong>{data.week_off ?? 0}</strong></span>
            <span>Holiday: <strong>{data.holiday ?? 0}</strong></span>
            <span>Absent: <strong>{data.absent ?? 0}</strong></span>
            {data.late_days != null && <span>Late: <strong>{data.late_days}</strong></span>}
            {data.miss_days != null && <span>Miss-punch: <strong>{data.miss_days}</strong></span>}
          </div>

          {/* Earnings breakdown */}
          <SectionTitle>Earnings Breakdown</SectionTitle>
          <div style={{ maxWidth: 420 }}>
            {!isPiece && data.basic != null && <SalaryRow label="Basic / Wage" value={money(data.basic)} />}
            {!isPiece && data.allowances != null && <SalaryRow label="Allowances" value={money(data.allowances)} />}
            {isPiece && data.piece_earnings != null && <SalaryRow label={`Piece Earnings (${data.units ?? 0} units)`} value={money(data.piece_earnings)} />}
            {data.overtime_amount != null && <SalaryRow label="Overtime" value={money(data.overtime_amount)} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 0', fontSize: 14, fontWeight: 800 }}>
              <span>Gross Earnings</span>
              <span style={{ color: HRC.navy }}>{money(data.gross_earnings)}</span>
            </div>
          </div>

          {data.method && (
            <div style={{ fontSize: 11, color: HRC.muted, marginTop: 14, fontStyle: 'italic' }}>
              Method: {data.method}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── EMPLOYEE DOCUMENTS ───────────────────────────────────────────────────────
// Upload, list and download documents held against an employee. The table and its rows have
// existed since the recruitment phase; until now they were only listable by candidate_id, so a
// document attached at hire was visible on the candidate and invisible on the person.
//
// doc_type is FREE TEXT with a datalist, not a select: the suggestions are a starting point,
// not a closed vocabulary, and the seventh kind of document somebody is handed must not be
// rejected by the form.
//
// WHO CAN SEE THIS: HR, admin and management. The subject employee CANNOT — the upload
// deliberately leaves file_registry.owner_user_id NULL so authorizeFile's owner branch does not
// admit them. Stated here as well as in the route so the omission reads the same from both ends.
function EmployeeDocuments({ employeeId }) {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [docType, setDocType] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    api.get(`/hr/employees/${employeeId}/documents`)
      .then(r => { setRows(r.data.data || []); setTypes(r.data.doc_types || []); setErr(''); })
      .catch(e => setErr(e.response?.data?.error || 'Could not load documents'));
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { setErr('Choose a file'); return; }
    if (!docType.trim()) { setErr('Say what kind of document this is'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType.trim());
      await api.post(`/hr/employees/${employeeId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFile(null); setDocType('');
      load();
    } catch (e) {
      setErr(e.response?.data?.error || 'Upload failed');
    } finally { setBusy(false); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div>
      <div style={{ background: '#f7f6f3', border: '1px solid #eee', borderRadius: 8, padding: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: HRC.navy, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
          Add a document
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 180px' }}>
            <label style={{ fontSize: 11, color: HRC.muted, display: 'block', marginBottom: 3 }}>Kind of document</label>
            <input list="emp-doc-types" value={docType} onChange={e => setDocType(e.target.value)}
                   placeholder="aadhaar, contract, …"
                   style={{ width: '100%', padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            <datalist id="emp-doc-types">{types.map(t => <option key={t} value={t} />)}</datalist>
          </div>
          <div style={{ flex: '2 1 240px' }}>
            <label style={{ fontSize: 11, color: HRC.muted, display: 'block', marginBottom: 3 }}>File</label>
            <input type="file" onChange={e => setFile(e.target.files?.[0] || null)} style={{ fontSize: 12 }} />
          </div>
          <button onClick={upload} disabled={busy}
                  style={{ padding: '8px 18px', background: HRC.navy, color: '#fff', border: 'none', borderRadius: 6,
                           fontSize: 13, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
        {err && <div style={{ marginTop: 10, padding: '7px 11px', background: '#FEE2E2', color: '#991B1B', borderRadius: 6, fontSize: 12.5 }}>{err}</div>}
      </div>

      {rows.length === 0 ? <Empty>No documents held for this employee.</Empty> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: '#f7f6f3', textAlign: 'left' }}>
            {['Kind', 'File', 'Uploaded', 'By', ''].map(h => (
              <th key={h} style={{ padding: '8px 10px', fontWeight: 700, color: '#555' }}>{h}</th>))}
          </tr></thead>
          <tbody>
            {rows.map(d => (
              <tr key={d.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                <td style={{ padding: '7px 10px', fontWeight: 600 }}>{d.doc_type}</td>
                <td style={{ padding: '7px 10px', color: HRC.muted }}>{d.original_name || '—'}</td>
                {/* created_at + uploaded_by ARE the history — no separate audit table needed. */}
                <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>{fmt(d.created_at)}</td>
                <td style={{ padding: '7px 10px' }}>{d.uploaded_by_name || '—'}</td>
                <td style={{ padding: '7px 10px' }}>
                  {/* Opens in a new tab; the auth_token cookie carries the session, which is why
                      no header is needed and no token appears in the URL. */}
                  <a href={d.file_url} target="_blank" rel="noreferrer"
                     style={{ color: HRC.navy, fontWeight: 600, textDecoration: 'none' }}>View / download</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
